/**
 * HTTP implementation of AdminService interface
 * Handles authenticated requests to the backend admin endpoints
 */

import {
  AdminService,
  User,
  CreateUserRequest,
  UpdateUserRequest,
  Organization,
  CreateOrganizationRequest,
  UpdateOrganizationRequest,
  AgentPrompt,
  UpdateAgentPromptRequest,
  AddPromptVersionRequest,
  SyncFromCodeResponse,
  UsageMetrics,
  PerformanceMetrics,
  HealthSummary,
  CostPeriod,
  CostSummary,
  CostTrendsResponse,
  TopCostUsersResponse,
  InfrastructureCostSummary,
  InfrastructureCostTrendsResponse,
  CombinedCostSummary,
} from './AdminService';
import { jsonAuthHeaders, AUTH_FETCH_INIT } from '../auth/apiAuth';
import { AppError, NetworkError } from '../../types/api';
import Logger from '../../utils/logger';

export class HttpAdminAdapter implements AdminService {
  private readonly apiBaseUrl: string;
  private logger = Logger.create('HttpAdminAdapter');

  constructor() {
    // Use environment variable with fallback for development
    this.apiBaseUrl = import.meta.env.VITE_API_BASE_URL || '/api/v1';
  }

  /**
   * Get authentication headers with Firebase ID token
   */
  private async getAuthHeaders(): Promise<Record<string, string>> {
    try {
      // task_111: provider-aware. Firebase attaches a Bearer token; Studio
      // omits it (the gml_sso cookie carries auth, sent via credentials:
      // 'include'). No longer throws on a missing token.
      return await jsonAuthHeaders();
    } catch (error) {
      this.logger.error(
        '[HttpAdminAdapter] Error getting auth headers:',
        error
      );
      const authError: AppError = {
        name: 'AuthError',
        message: 'Authentication failed',
        type: 'auth',
        code: 'auth/failed',
        details: error,
      };
      throw authError;
    }
  }

  /**
   * Generic method to make authenticated requests
   */
  private async fetchWithAuth<T>(
    url: string,
    options: RequestInit = {}
  ): Promise<T> {
    try {
      const headers = await this.getAuthHeaders();

      const response = await fetch(url, {
        ...AUTH_FETCH_INIT,
        ...options,
        headers: {
          ...headers,
          ...options.headers,
        },
      });

      if (!response.ok) {
        let errorMessage = response.statusText || 'Unknown error';
        let errorCode: string | undefined;
        let errorDetails: Record<string, unknown> | undefined = undefined;

        try {
          const errorData = await response.json();

          if (errorData?.error) {
            // New standardized format: { error: { message, error_code, details } }
            errorMessage = errorData.error.message || errorMessage;
            errorCode = errorData.error.error_code;
            errorDetails = errorData.error.details;
          } else if (errorData?.detail) {
            // Legacy format: { detail: "message" }
            errorMessage = errorData.detail;
          }
        } catch (parseError) {
          // If we can't parse the error response, use the status text
          this.logger.warn(
            '[HttpAdminAdapter] Could not parse error response:',
            parseError
          );
        }

        // Create enhanced error object with status and context
        const error: AppError = {
          name: 'ApiError',
          message: errorMessage,
          type: 'api',
          status: response.status,
          details: {
            url,
            method: options.method || 'GET',
            errorCode,
            serverDetails: errorDetails,
          },
        };

        throw error;
      }

      return response.json();
    } catch (error) {
      // Handle network errors and other exceptions
      if (error instanceof TypeError && error.message.includes('fetch')) {
        const networkError: NetworkError = {
          name: 'NetworkError',
          message: 'Network connection failed',
          url,
        };
        throw networkError;
      }

      // Re-throw errors with additional context
      throw error;
    }
  }

  // ============================================
  // User Management
  // ============================================

  /**
   * Fetch all users from the backend (admin only)
   */
  async listUsers(): Promise<User[]> {
    try {
      this.logger.debug('[HttpAdminAdapter] Fetching users...');
      const users = await this.fetchWithAuth<User[]>(
        `${this.apiBaseUrl}/admin/users`
      );
      this.logger.debug(
        `[HttpAdminAdapter] Successfully fetched ${users.length} users`
      );
      return users;
    } catch (error) {
      this.logger.error('[HttpAdminAdapter] Error fetching users:', error);
      throw error;
    }
  }

  /**
   * Create a new user
   */
  async createUser(data: CreateUserRequest): Promise<User> {
    try {
      this.logger.debug('[HttpAdminAdapter] Creating user:', data.email);
      const user = await this.fetchWithAuth<User>(
        `${this.apiBaseUrl}/admin/users`,
        {
          method: 'POST',
          body: JSON.stringify(data),
        }
      );
      this.logger.debug(
        `[HttpAdminAdapter] Successfully created user: ${user.uid}`
      );
      return user;
    } catch (error) {
      this.logger.error('[HttpAdminAdapter] Error creating user:', error);
      throw error;
    }
  }

  /**
   * Update an existing user
   */
  async updateUser(uid: string, data: UpdateUserRequest): Promise<User> {
    try {
      this.logger.debug('[HttpAdminAdapter] Updating user:', uid);
      const user = await this.fetchWithAuth<User>(
        `${this.apiBaseUrl}/admin/users/${uid}`,
        {
          method: 'PUT',
          body: JSON.stringify(data),
        }
      );
      this.logger.debug(`[HttpAdminAdapter] Successfully updated user: ${uid}`);
      return user;
    } catch (error) {
      this.logger.error('[HttpAdminAdapter] Error updating user:', error);
      throw error;
    }
  }

  /**
   * Delete (deactivate) a user
   */
  async deleteUser(uid: string): Promise<void> {
    try {
      this.logger.debug('[HttpAdminAdapter] Deleting user:', uid);
      await this.fetchWithAuth<{ message: string }>(
        `${this.apiBaseUrl}/admin/users/${uid}`,
        {
          method: 'DELETE',
        }
      );
      this.logger.debug(`[HttpAdminAdapter] Successfully deleted user: ${uid}`);
    } catch (error) {
      this.logger.error('[HttpAdminAdapter] Error deleting user:', error);
      throw error;
    }
  }

  // ============================================
  // Organization Management
  // ============================================

  /**
   * Fetch all organizations
   */
  async listOrganizations(): Promise<Organization[]> {
    try {
      this.logger.debug('[HttpAdminAdapter] Fetching organizations...');
      const organizations = await this.fetchWithAuth<Organization[]>(
        `${this.apiBaseUrl}/admin/organizations`
      );
      this.logger.debug(
        `[HttpAdminAdapter] Successfully fetched ${organizations.length} organizations`
      );
      return organizations;
    } catch (error) {
      this.logger.error(
        '[HttpAdminAdapter] Error fetching organizations:',
        error
      );
      throw error;
    }
  }

  /**
   * Get a specific organization
   */
  async getOrganization(orgId: string): Promise<Organization> {
    try {
      this.logger.debug('[HttpAdminAdapter] Fetching organization:', orgId);
      const organization = await this.fetchWithAuth<Organization>(
        `${this.apiBaseUrl}/admin/organizations/${orgId}`
      );
      this.logger.debug(
        `[HttpAdminAdapter] Successfully fetched organization: ${orgId}`
      );
      return organization;
    } catch (error) {
      this.logger.error(
        '[HttpAdminAdapter] Error fetching organization:',
        error
      );
      throw error;
    }
  }

  /**
   * Create a new organization
   */
  async createOrganization(
    data: CreateOrganizationRequest
  ): Promise<Organization> {
    try {
      this.logger.debug('[HttpAdminAdapter] Creating organization:', data.name);
      const organization = await this.fetchWithAuth<Organization>(
        `${this.apiBaseUrl}/admin/organizations`,
        {
          method: 'POST',
          body: JSON.stringify(data),
        }
      );
      this.logger.debug(
        `[HttpAdminAdapter] Successfully created organization: ${organization.id}`
      );
      return organization;
    } catch (error) {
      this.logger.error(
        '[HttpAdminAdapter] Error creating organization:',
        error
      );
      throw error;
    }
  }

  /**
   * Update an existing organization
   */
  async updateOrganization(
    orgId: string,
    data: UpdateOrganizationRequest
  ): Promise<Organization> {
    try {
      this.logger.debug('[HttpAdminAdapter] Updating organization:', orgId);
      const organization = await this.fetchWithAuth<Organization>(
        `${this.apiBaseUrl}/admin/organizations/${orgId}`,
        {
          method: 'PUT',
          body: JSON.stringify(data),
        }
      );
      this.logger.debug(
        `[HttpAdminAdapter] Successfully updated organization: ${orgId}`
      );
      return organization;
    } catch (error) {
      this.logger.error(
        '[HttpAdminAdapter] Error updating organization:',
        error
      );
      throw error;
    }
  }

  /**
   * Delete an organization
   */
  async deleteOrganization(orgId: string): Promise<void> {
    try {
      this.logger.debug('[HttpAdminAdapter] Deleting organization:', orgId);
      await this.fetchWithAuth<{ message: string }>(
        `${this.apiBaseUrl}/admin/organizations/${orgId}`,
        {
          method: 'DELETE',
        }
      );
      this.logger.debug(
        `[HttpAdminAdapter] Successfully deleted organization: ${orgId}`
      );
    } catch (error) {
      this.logger.error(
        '[HttpAdminAdapter] Error deleting organization:',
        error
      );
      throw error;
    }
  }

  /**
   * Get users in an organization
   */
  async getOrganizationUsers(orgId: string): Promise<User[]> {
    try {
      this.logger.debug(
        '[HttpAdminAdapter] Fetching organization users:',
        orgId
      );
      const users = await this.fetchWithAuth<User[]>(
        `${this.apiBaseUrl}/admin/organizations/${orgId}/users`
      );
      this.logger.debug(
        `[HttpAdminAdapter] Successfully fetched ${users.length} users for organization: ${orgId}`
      );
      return users;
    } catch (error) {
      this.logger.error(
        '[HttpAdminAdapter] Error fetching organization users:',
        error
      );
      throw error;
    }
  }

  /**
   * Add a user to an organization
   */
  async addUserToOrganization(orgId: string, uid: string): Promise<void> {
    try {
      this.logger.debug('[HttpAdminAdapter] Adding user to organization:', {
        orgId,
        uid,
      });
      await this.fetchWithAuth<{ message: string }>(
        `${this.apiBaseUrl}/admin/organizations/${orgId}/users/${uid}`,
        {
          method: 'POST',
        }
      );
      this.logger.debug(
        `[HttpAdminAdapter] Successfully added user ${uid} to organization ${orgId}`
      );
    } catch (error) {
      this.logger.error(
        '[HttpAdminAdapter] Error adding user to organization:',
        error
      );
      throw error;
    }
  }

  /**
   * Remove a user from an organization
   */
  async removeUserFromOrganization(orgId: string, uid: string): Promise<void> {
    try {
      this.logger.debug('[HttpAdminAdapter] Removing user from organization:', {
        orgId,
        uid,
      });
      await this.fetchWithAuth<{ message: string }>(
        `${this.apiBaseUrl}/admin/organizations/${orgId}/users/${uid}`,
        {
          method: 'DELETE',
        }
      );
      this.logger.debug(
        `[HttpAdminAdapter] Successfully removed user ${uid} from organization ${orgId}`
      );
    } catch (error) {
      this.logger.error(
        '[HttpAdminAdapter] Error removing user from organization:',
        error
      );
      throw error;
    }
  }

  // ============================================
  // Agent Prompt Management
  // ============================================

  /**
   * Fetch all agent prompts
   */
  async listAgentPrompts(): Promise<AgentPrompt[]> {
    try {
      this.logger.debug('[HttpAdminAdapter] Fetching agent prompts...');
      const prompts = await this.fetchWithAuth<AgentPrompt[]>(
        `${this.apiBaseUrl}/admin/agent-prompts`
      );
      this.logger.debug(
        `[HttpAdminAdapter] Successfully fetched ${prompts.length} agent prompts`
      );
      return prompts;
    } catch (error) {
      this.logger.error(
        '[HttpAdminAdapter] Error fetching agent prompts:',
        error
      );
      throw error;
    }
  }

  /**
   * Get a specific agent prompt
   */
  async getAgentPrompt(agentName: string): Promise<AgentPrompt> {
    try {
      this.logger.debug('[HttpAdminAdapter] Fetching agent prompt:', agentName);
      const prompt = await this.fetchWithAuth<AgentPrompt>(
        `${this.apiBaseUrl}/admin/agent-prompts/${agentName}`
      );
      this.logger.debug(
        `[HttpAdminAdapter] Successfully fetched agent prompt: ${agentName}`
      );
      return prompt;
    } catch (error) {
      this.logger.error(
        '[HttpAdminAdapter] Error fetching agent prompt:',
        error
      );
      throw error;
    }
  }

  /**
   * Update an agent prompt
   */
  async updateAgentPrompt(
    agentName: string,
    data: UpdateAgentPromptRequest
  ): Promise<AgentPrompt> {
    try {
      this.logger.debug('[HttpAdminAdapter] Updating agent prompt:', agentName);
      const prompt = await this.fetchWithAuth<AgentPrompt>(
        `${this.apiBaseUrl}/admin/agent-prompts/${agentName}`,
        {
          method: 'PUT',
          body: JSON.stringify(data),
        }
      );
      this.logger.debug(
        `[HttpAdminAdapter] Successfully updated agent prompt: ${agentName}`
      );
      return prompt;
    } catch (error) {
      this.logger.error(
        '[HttpAdminAdapter] Error updating agent prompt:',
        error
      );
      throw error;
    }
  }

  /**
   * Add a new version to an agent prompt
   */
  async addAgentPromptVersion(
    agentName: string,
    data: AddPromptVersionRequest
  ): Promise<AgentPrompt> {
    try {
      this.logger.debug(
        '[HttpAdminAdapter] Adding version to agent prompt:',
        agentName
      );
      const prompt = await this.fetchWithAuth<AgentPrompt>(
        `${this.apiBaseUrl}/admin/agent-prompts/${agentName}/versions`,
        {
          method: 'POST',
          body: JSON.stringify(data),
        }
      );
      this.logger.debug(
        `[HttpAdminAdapter] Successfully added version to agent prompt: ${agentName}`
      );
      return prompt;
    } catch (error) {
      this.logger.error(
        '[HttpAdminAdapter] Error adding version to agent prompt:',
        error
      );
      throw error;
    }
  }

  /**
   * Rollback an agent prompt to a previous version
   */
  async rollbackAgentPromptVersion(
    agentName: string,
    version: string
  ): Promise<AgentPrompt> {
    try {
      this.logger.debug('[HttpAdminAdapter] Rolling back agent prompt:', {
        agentName,
        version,
      });
      const prompt = await this.fetchWithAuth<AgentPrompt>(
        `${this.apiBaseUrl}/admin/agent-prompts/${agentName}/rollback`,
        {
          method: 'POST',
          body: JSON.stringify({ version }),
        }
      );
      this.logger.debug(
        `[HttpAdminAdapter] Successfully rolled back agent prompt ${agentName} to version ${version}`
      );
      return prompt;
    } catch (error) {
      this.logger.error(
        '[HttpAdminAdapter] Error rolling back agent prompt:',
        error
      );
      throw error;
    }
  }

  /**
   * Sync agent prompts from code to Firestore
   */
  async syncAgentPromptsFromCode(
    preview: boolean = false
  ): Promise<SyncFromCodeResponse> {
    try {
      this.logger.debug('[HttpAdminAdapter] Syncing agent prompts from code:', {
        preview,
      });
      const response = await this.fetchWithAuth<SyncFromCodeResponse>(
        `${this.apiBaseUrl}/admin/agent-prompts/sync-from-code?preview=${preview}`,
        {
          method: 'POST',
        }
      );
      this.logger.debug(
        `[HttpAdminAdapter] Sync complete: ${response.agents_synced} synced, ${response.agents_with_changes} with changes`
      );
      return response;
    } catch (error) {
      this.logger.error(
        '[HttpAdminAdapter] Error syncing agent prompts from code:',
        error
      );
      throw error;
    }
  }

  // ============================================
  // Analytics & Monitoring
  // ============================================

  /**
   * Get usage metrics
   */
  async getUsageMetrics(refresh: boolean = false): Promise<UsageMetrics> {
    try {
      this.logger.debug('[HttpAdminAdapter] Fetching usage metrics...');
      const url = refresh
        ? `${this.apiBaseUrl}/admin/analytics?refresh=true`
        : `${this.apiBaseUrl}/admin/analytics`;
      const metrics = await this.fetchWithAuth<UsageMetrics>(url);
      this.logger.debug(
        '[HttpAdminAdapter] Successfully fetched usage metrics'
      );
      return metrics;
    } catch (error) {
      this.logger.error(
        '[HttpAdminAdapter] Error fetching usage metrics:',
        error
      );
      throw error;
    }
  }

  /**
   * Get performance metrics
   */
  async getPerformanceMetrics(): Promise<PerformanceMetrics> {
    try {
      this.logger.debug('[HttpAdminAdapter] Fetching performance metrics...');
      const metrics = await this.fetchWithAuth<PerformanceMetrics>(
        `${this.apiBaseUrl}/admin/analytics/performance`
      );
      this.logger.debug(
        '[HttpAdminAdapter] Successfully fetched performance metrics'
      );
      return metrics;
    } catch (error) {
      this.logger.error(
        '[HttpAdminAdapter] Error fetching performance metrics:',
        error
      );
      throw error;
    }
  }

  /**
   * Get health summary for Admin Console heads-up display (Task 060a)
   */
  async getHealthSummary(): Promise<HealthSummary> {
    try {
      this.logger.debug('[HttpAdminAdapter] Fetching health summary...');
      const summary = await this.fetchWithAuth<HealthSummary>(
        `${this.apiBaseUrl}/admin/monitoring/health-summary`
      );
      this.logger.debug(
        '[HttpAdminAdapter] Successfully fetched health summary'
      );
      return summary;
    } catch (error) {
      this.logger.error(
        '[HttpAdminAdapter] Error fetching health summary:',
        error
      );
      throw error;
    }
  }

  // ============================================
  // Cost Monitoring (Task 080)
  // ============================================

  /**
   * Get platform cost summary for a time period
   */
  async getCostSummary(
    period: CostPeriod = 'month',
    orgId?: string
  ): Promise<CostSummary> {
    try {
      this.logger.debug('[HttpAdminAdapter] Fetching cost summary...');
      const params = new URLSearchParams({ period });
      if (orgId) {
        params.append('org_id', orgId);
      }
      const summary = await this.fetchWithAuth<CostSummary>(
        `${this.apiBaseUrl}/admin/costs/summary?${params.toString()}`
      );
      this.logger.debug('[HttpAdminAdapter] Successfully fetched cost summary');
      return summary;
    } catch (error) {
      this.logger.error(
        '[HttpAdminAdapter] Error fetching cost summary:',
        error
      );
      throw error;
    }
  }

  /**
   * Get cost trends over time
   */
  async getCostTrends(days: number = 30): Promise<CostTrendsResponse> {
    try {
      this.logger.debug('[HttpAdminAdapter] Fetching cost trends...');
      const response = await this.fetchWithAuth<CostTrendsResponse>(
        `${this.apiBaseUrl}/admin/costs/trends?days=${days}`
      );
      this.logger.debug('[HttpAdminAdapter] Successfully fetched cost trends');
      return response;
    } catch (error) {
      this.logger.error(
        '[HttpAdminAdapter] Error fetching cost trends:',
        error
      );
      throw error;
    }
  }

  /**
   * Get top users by cost
   */
  async getTopCostUsers(
    limit: number = 10,
    period: CostPeriod = 'month'
  ): Promise<TopCostUsersResponse> {
    try {
      this.logger.debug('[HttpAdminAdapter] Fetching top cost users...');
      const params = new URLSearchParams({
        limit: limit.toString(),
        period,
      });
      const response = await this.fetchWithAuth<TopCostUsersResponse>(
        `${this.apiBaseUrl}/admin/costs/top-users?${params.toString()}`
      );
      this.logger.debug(
        '[HttpAdminAdapter] Successfully fetched top cost users'
      );
      return response;
    } catch (error) {
      this.logger.error(
        '[HttpAdminAdapter] Error fetching top cost users:',
        error
      );
      throw error;
    }
  }

  /**
   * Get costs by workflow step
   */
  async getCostsByStep(
    period: CostPeriod = 'month'
  ): Promise<Record<string, number>> {
    try {
      this.logger.debug('[HttpAdminAdapter] Fetching costs by step...');
      const response = await this.fetchWithAuth<Record<string, number>>(
        `${this.apiBaseUrl}/admin/costs/by-step?period=${period}`
      );
      this.logger.debug(
        '[HttpAdminAdapter] Successfully fetched costs by step'
      );
      return response;
    } catch (error) {
      this.logger.error(
        '[HttpAdminAdapter] Error fetching costs by step:',
        error
      );
      throw error;
    }
  }

  /**
   * Get costs by organization
   */
  async getCostsByOrganization(
    period: CostPeriod = 'month'
  ): Promise<Record<string, number>> {
    try {
      this.logger.debug('[HttpAdminAdapter] Fetching costs by organization...');
      const response = await this.fetchWithAuth<Record<string, number>>(
        `${this.apiBaseUrl}/admin/costs/by-organization?period=${period}`
      );
      this.logger.debug(
        '[HttpAdminAdapter] Successfully fetched costs by organization'
      );
      return response;
    } catch (error) {
      this.logger.error(
        '[HttpAdminAdapter] Error fetching costs by organization:',
        error
      );
      throw error;
    }
  }

  // Infrastructure Cost Monitoring (Task 081)

  /**
   * Get GCP infrastructure cost summary
   */
  async getInfrastructureCostSummary(
    period: CostPeriod = 'month'
  ): Promise<InfrastructureCostSummary> {
    try {
      this.logger.debug(
        '[HttpAdminAdapter] Fetching infrastructure cost summary...'
      );
      const response = await this.fetchWithAuth<InfrastructureCostSummary>(
        `${this.apiBaseUrl}/admin/costs/infrastructure/summary?period=${period}`
      );
      this.logger.debug(
        '[HttpAdminAdapter] Successfully fetched infrastructure costs'
      );
      return response;
    } catch (error) {
      this.logger.error(
        '[HttpAdminAdapter] Error fetching infrastructure costs:',
        error
      );
      throw error;
    }
  }

  /**
   * Get infrastructure cost trends over time
   */
  async getInfrastructureCostTrends(
    days: number = 30
  ): Promise<InfrastructureCostTrendsResponse> {
    try {
      this.logger.debug(
        '[HttpAdminAdapter] Fetching infrastructure cost trends...'
      );
      const response =
        await this.fetchWithAuth<InfrastructureCostTrendsResponse>(
          `${this.apiBaseUrl}/admin/costs/infrastructure/trends?days=${days}`
        );
      this.logger.debug(
        '[HttpAdminAdapter] Successfully fetched infrastructure trends'
      );
      return response;
    } catch (error) {
      this.logger.error(
        '[HttpAdminAdapter] Error fetching infrastructure trends:',
        error
      );
      throw error;
    }
  }

  /**
   * Get combined API + infrastructure cost summary
   */
  async getCombinedCostSummary(
    period: CostPeriod = 'month'
  ): Promise<CombinedCostSummary> {
    try {
      this.logger.debug('[HttpAdminAdapter] Fetching combined cost summary...');
      const response = await this.fetchWithAuth<CombinedCostSummary>(
        `${this.apiBaseUrl}/admin/costs/combined/summary?period=${period}`
      );
      this.logger.debug(
        '[HttpAdminAdapter] Successfully fetched combined costs'
      );
      return response;
    } catch (error) {
      this.logger.error(
        '[HttpAdminAdapter] Error fetching combined costs:',
        error
      );
      throw error;
    }
  }
}

// Singleton instance for shared use across components
export const adminService = new HttpAdminAdapter();
